<script>
import { mapGetters, mapActions } from 'vuex'
import CandidateBookings from '@/components/candidate/candidate-bookings.vue'
import CandidateCertifications from '@/components/candidate/candidate-certifications.vue'
import CandidateQualifications from '@/components/candidate/candidate-qualifications.vue'
import UserAvatar from '@/components/user/user-avatar.vue'
import baseMixin from '@/mixins/baseMixin'

export default {
  name: 'CandidateZoom',
  components: {
    CandidateBookings,
    CandidateCertifications,
    CandidateQualifications,
    UserAvatar,
  },
  mixins: [baseMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data: () => {
    return {
      hasLoadedSuccessfully: false,
      tab: null,
      candidate: null,
      displayPic: null,
    }
  },
  computed: {
    ...mapGetters({
      candidateDisplayPicture: 'candidate/candidateDisplayPicture',
      isLoading: 'candidate/isLoading',
    }),
    candidateName() {
      return this.candidate ? this.candidate?.fullName : `- -`
    },
    profilePicture() {
      const picture = this.candidateDisplayPicture(this.id)
      return `data:image/jpeg;base64,${picture}` || null
    },
    bookings() {
      if (!this.hasLoadedSuccessfully) return null
      return this.candidate.bookings
    },
    numberOfBookings() {
      if (!this.hasLoadedSuccessfully) return 0
      return this.bookings.length
    },
    certifications() {
      if (!this.hasLoadedSuccessfully) return null
      return this.candidate.certifications
    },
    numberOfCertifications() {
      if (!this.hasLoadedSuccessfully) return 0
      return this.certifications.length
    },
    qualifications() {
      if (!this.hasLoadedSuccessfully) return null
      return this.candidate.qualifications
    },
    numberOfQualifications() {
      if (!this.hasLoadedSuccessfully) return 0
      return this.qualifications.length
    },
  },
  async created() {
    const candidateResponse = await this.loadCandidate(this.id)
    if (candidateResponse.isSuccess) {
      this.hasLoadedSuccessfully = true
      this.candidate = candidateResponse.data
    }
  },
  methods: {
    ...mapActions({
      loadCandidate: 'candidate/loadCandidate',
    }),
    closeCandidateZoom() {
      this.tab = null
      this.candidate = null
      this.displayPic = null
      this.$emit('closeModal')
    },
  },
}
</script>

<template>
  <v-card :color="colorClass.blue">
    <v-btn
      class="ma-2 float-right close-button"
      icon
      dark
      @click="closeCandidateZoom"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>
    <v-card-title
      class="text-center align-center justify-center flex-column py-4"
    >
      <UserAvatar
        transparent_outline
        :name="candidateName"
        :image-url="profilePicture"
        size="xxl"
        class="mb-4"
      />
      <v-card-title class="pa-0 white--text">
        <div v-if="isLoading" class="d-flex">
          <v-skeleton-loader
            loading
            style="width: 240px; height: 40px;"
            class="zoom-skeleton-title"
            type="image"
          ></v-skeleton-loader>
        </div>
        <span v-else>{{ candidateName }}</span></v-card-title
      >
    </v-card-title>
    <v-tabs v-model="tab" background-color="blue" dark fixed-tabs>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-bookings">
        <v-badge
          color="green"
          :value="hasLoadedSuccessfully && numberOfBookings > 0"
          :content="numberOfBookings > 0 ? candidate.bookings.length : 0"
        >
          Placements
        </v-badge>
      </v-tab>
      <v-tab href="#tab-certifications">
        <v-badge
          color="green"
          :value="hasLoadedSuccessfully && numberOfCertifications > 0"
          :content="
            numberOfCertifications > 0 ? candidate.certifications.length : 0
          "
        >
          Certifications
        </v-badge>
      </v-tab>
      <v-tab href="#tab-qualifications">
        <v-badge
          color="green"
          :value="hasLoadedSuccessfully && numberOfQualifications > 0"
          :content="
            numberOfQualifications > 0 ? candidate.qualifications.length : 0
          "
        >
          Qualifications
        </v-badge>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item :value="'tab-bookings'">
        <v-skeleton-loader
          v-if="!hasLoadedSuccessfully"
          loading
          type="booking-tab"
          :types="{
            'booking-tab': 'year-text, booking-row@5',
            'year-text': 'text',
            'booking-row': 'text, text',
          }"
          class="zoom-skeleton-tab"
        ></v-skeleton-loader>
        <CandidateBookings
          v-else
          :bookings="bookings"
          :time-zone="candidate.timeZone"
        />
      </v-tab-item>
      <v-tab-item :value="'tab-certifications'">
        <v-skeleton-loader
          v-if="!hasLoadedSuccessfully"
          loading
          type="zoom-table@3, table-row"
          :types="{
            'zoom-table': 'table-row, divider',
            'table-row': 'table-cell@4',
          }"
          class="zoom-skeleton-tab"
        ></v-skeleton-loader>
        <CandidateCertifications
          v-else
          :certifications="certifications"
          :time-zone="candidate.timeZone"
        />
      </v-tab-item>
      <v-tab-item :value="'tab-qualifications'">
        <v-skeleton-loader
          v-if="!hasLoadedSuccessfully"
          loading
          type="zoom-table@3, table-row"
          :types="{
            'zoom-table': 'table-row, divider',
            'table-row': 'table-cell@4',
          }"
          class="zoom-skeleton-tab"
        ></v-skeleton-loader>
        <CandidateQualifications
          v-else
          :qualifications="qualifications"
          :time-zone="candidate.timeZone"
        />
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<style lang="scss" scoped>
@import '@styles';
.close-button {
  position: absolute;
  right: 0;
}

::v-deep.zoom-skeleton-title.v-skeleton-loader--is-loading {
  .v-skeleton-loader__avatar {
    width: 150px; // change width or height here
    height: 150px;
    margin-bottom: 16px;
  }
  .v-skeleton-loader__text {
    height: 1.25rem;
  }
}

::v-deep.zoom-skeleton-tab.v-skeleton-loader--is-loading {
  padding: 40px;
  .v-skeleton-loader__booking-row {
    display: flex;
    padding-top: 3px;
    margin-bottom: 5px;
  }

  .v-skeleton-loader__booking-tab {
    padding: 10px;
    margin-bottom: 10px;
  }

  .v-skeleton-loader__year-text .v-skeleton-loader__text {
    width: 50px;
    margin-right: auto;
    margin-left: auto;
  }

  .v-skeleton-loader__booking-row .v-skeleton-loader__text:first-child {
    flex: 1;
  }

  .v-skeleton-loader__booking-row .v-skeleton-loader__text:last-child {
    flex: 5;
  }
}
</style>
